import { type IdNameObject } from '@m/types';
import {
  AutoChargeVisitConfig,
  BigMacDevice,
  LocationGateActions,
  LocationLane,
  PedestrianDoorUpsertPayload,
} from '@m/types/api';
import { apiFetch } from '@m/utils/http';
import { APPROACH_SETTING_IGNORE } from 'apps/admin/constants/Camera';

// TODO: replace `simulateApiFetch()` with `apiFetch()` when API is ready

const defaultCameraConfig = {
  topBound: 0,
  leftBound: 0,
  rightBound: 1,
  bottomBound: 1,
  minIntersection: 0,
  approachSettingType: APPROACH_SETTING_IGNORE,
};

export type AddLaneCameraPayload = {
  active: boolean;
  approachSettingType?: IdNameObject;
  directedLaneId: number;
  linkedSiteId?: number | null;
  model?: string | null;
  notes: string | null;
  serial: string | null;
  siteEquipmentType?: IdNameObject;
};

export type UpdateLaneCameraPayload = {
  active?: boolean;
  approachSettingType?: IdNameObject;
  directedLaneId: number;
  enabled?: boolean;
  id: number;
  linkedSiteId?: number | null;
  make?: string;
  model?: string | null;
  notes?: string | null;
  serial: string | null;
  siteEquipmentType?: IdNameObject;
};

export const LaneService = {
  /**
   * Get all lanes for a location
   */
  getLanes: ({ locationId }: { locationId: number }) =>
    apiFetch<{ attributes: LocationGateActions; lanes: LocationLane[] }>(
      `/admin/site/${locationId}/lane`,
    ),

  /**
   * Get location lane details
   */
  getLaneDetail: ({ locationId, laneId }: { laneId: number; locationId: number }) =>
    apiFetch(`/admin/site/${locationId}/lane/${laneId}`),

  /**
   * Get location Gate details
   */
  getGateDetail: ({ locationId, laneId }: { laneId: number; locationId: number }) =>
    apiFetch(`/admin/sites/${locationId}/lanes/${laneId}/gates`),

  /**
   * Create a new lane
   */
  updateLane: ({
    locationId,
    laneId,
    body,
  }: {
    body: {
      autoChargeVisitConfig?: AutoChargeVisitConfig;
      name: string;
    };
    laneId: number;
    locationId: number;
  }) =>
    apiFetch(`/admin/site/${locationId}/lane/${laneId}`, {
      method: 'POST',
      body,
    }),

  addLaneCamera: ({
    locationId,
    laneId,
    body: { approachSettingType, ...body },
  }: {
    body: AddLaneCameraPayload;
    laneId: number;
    locationId: number;
  }) =>
    apiFetch(`/admin/location/${locationId}/equipment`, {
      method: 'POST',
      body: {
        ...body,
        laneId,
        config: {
          ...defaultCameraConfig,
          approachSettingType,
        },
      },
    }),

  updateLaneCamera: ({
    locationId,
    laneId,
    body: { approachSettingType, ...body },
  }: {
    body: UpdateLaneCameraPayload;
    laneId: number;
    locationId: number;
  }) =>
    apiFetch(`/admin/location/${locationId}/equipment/${body.id}`, {
      method: 'PUT',
      body: {
        ...body,
        laneId,
        approachSettingType,
        config: {
          ...defaultCameraConfig,
          approachSettingType,
        },
      },
    }),

  removeLaneCamera: ({
    locationId,
    laneId,
    cameraId,
  }: {
    cameraId: number;
    laneId: number;
    locationId: number;
  }) => apiFetch(`/admin/location/${locationId}/equipment/${cameraId}`, { method: 'DELETE' }),

  getDoors: ({ locationId }: { locationId: number }) => apiFetch(`/admin/site/${locationId}/doors`),

  openDoor: ({ locationId, doorId }: { doorId: number; locationId: number }) =>
    apiFetch(`/admin/site/${locationId}/doors/${doorId}/open`, { method: 'POST' }),

  createDoor: ({ locationId, body }: { body: PedestrianDoorUpsertPayload; locationId: number }) =>
    apiFetch(`/admin/site/${locationId}/doors`, {
      method: 'PUT',
      body: [body],
    }),

  getLaneDevices: ({ locationId }: { locationId: number }) =>
    apiFetch(`/admin/sites/${locationId}/devices`, {
      method: 'GET',
    }),

  adoptLaneDevice: ({
    siteId,
    deviceUuid,
    body,
  }: {
    body: BigMacDevice;
    deviceUuid: string;
    siteId: number;
  }) =>
    apiFetch(`/admin/sites/${siteId}/devices/${deviceUuid}`, {
      method: 'PUT',
      body,
    }),

  updateLaneDevice: ({ body }: { body: BigMacDevice }) =>
    apiFetch(`/ehcs/devices/${body.uuid}/update`, {
      method: 'PUT',
      body,
    }),

  abandonLaneDevice: ({ siteId, deviceId }: { deviceId: string; siteId: number }) =>
    apiFetch(`/admin/sites/${siteId}/devices/${deviceId}`, {
      method: 'DELETE',
    }),

  addGate: ({
    payload,
    siteId,
    laneId,
  }: {
    laneId: number;
    payload: {
      controlDefaults: {
        pulseDelayMs: number;
        pulseLengthMs: number;
      };
      deviceRelayNum: number;
      deviceUuid: string;
      enabled: boolean;
      gateActions: {
        automatic: boolean;
        nestedZone: boolean;
        parkingPass: boolean;
        reservation: boolean;
        subscriber: boolean;
        textToPay: boolean;
        textToVend: boolean;
        valetExit: boolean;
      };
      laneId: number;
      notes: string;
    };
    siteId: number;
  }) =>
    apiFetch(`/admin/sites/${siteId}/lanes/${laneId}/gates`, {
      method: 'POST',
      body: payload,
    }),

  updateGate: ({
    payload,
    siteId,
    laneId,
    gateId,
  }: {
    gateId: number;
    laneId: number;
    payload: {
      controlDefaults: {
        pulseDelayMs: number;
        pulseLengthMs: number;
      };
      deviceRelayNum: number;
      deviceUuid: string;
      enabled: boolean;
      gateActions: {
        automatic: boolean;
        nestedZone: boolean;
        parkingPass: boolean;
        reservation: boolean;
        subscriber: boolean;
        textToPay: boolean;
        textToVend: boolean;
        valetExit: boolean;
      };
      laneId: number;
      notes: string;
    };
    siteId: number;
  }) =>
    apiFetch(`/admin/sites/${siteId}/lanes/${laneId}/gates/${gateId}`, {
      method: 'PUT',
      body: payload,
    }),

  removeGate: ({ siteId, laneId, gateId }: { gateId: number; laneId: number; siteId: number }) =>
    apiFetch(`/admin/sites/${siteId}/lanes/${laneId}/gates/${gateId}`, {
      method: 'DELETE',
    }),
};
